import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RateItem from '../components/reviews/RateItem';
import { getReviewLinkOrder, submitLinkReview } from '../api/real/reviewsApi';

const STATUS_TEXT = { PENDING: 'Thanks — it will appear after a quick check', APPROVED: 'Published', REJECTED: 'Not published' };

/**
 * /review?t=… — opened from the "How was your order?" e-mail. The link itself lets the customer rate that order's
 * items, without signing in (most customers check out as guests and have no password). ?preview=1 = the store's own
 * test e-mail.
 */
export default function ReviewLinkPage() {
  const [params] = useSearchParams();
  const token = params.get('t');
  const preview = params.get('preview') === '1';
  // the star the customer tapped in the e-mail: ?item=<order item id>&rating=1…5
  const tappedItem = Number(params.get('item')) || null;
  const tappedRating = Number(params.get('rating')) || 0;
  const tappedRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!token && !preview);

  const load = useCallback(async () => {
    if (!token || preview) return;
    try {
      setOrder(await getReviewLinkOrder(token));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || 'This link could not be opened. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, preview]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (order && tappedRef.current?.scrollIntoView) tappedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [order]);

  const items = order?.items || [];
  const allRated = items.length > 0 && items.every((i) => i.review);

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
      <Helmet>
        <title>Rate your order</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 className="font-display text-2xl sm:text-3xl text-ink mb-2">
        {order?.firstName ? `Hi ${order.firstName}, how was your order?` : 'How was your order?'}
      </h1>
      {order && <p className="text-sm text-ink-soft mb-6">Order {order.orderNumber}</p>}

      {preview && (
        <div className="card p-5 text-sm text-ink-soft" data-testid="review-preview">
          This is the page your customers see from the e-mail. In the real e-mail the button opens the customer's own
          order here, ready to rate — no sign-in needed.
        </div>
      )}

      {!preview && !token && (
        <div className="card p-5 text-sm text-ink-soft">
          This link is missing something. <Link to="/account/orders" className="text-primary font-semibold hover:underline">Sign in</Link> to
          rate your items from My Orders.
        </div>
      )}

      {loading && <div className="card p-5"><div className="shimmer h-16 rounded-xl" /></div>}

      {error && (
        <div className="card p-5 text-sm text-ink-soft" role="alert">
          {error}{' '}
          <Link to="/account/orders" className="text-primary font-semibold hover:underline">Go to My Orders</Link>
        </div>
      )}

      {order && !order.delivered && (
        <div className="card p-5 text-sm text-ink-soft">You can rate your items once this order has been delivered.</div>
      )}

      {order && order.delivered && (
        <>
          {allRated && (
            <div className="card p-5 mb-4 bg-primary-50 border-primary-light text-sm text-ink" data-testid="all-rated">
              Thank you for rating your order! Your ratings appear after a quick check.
            </div>
          )}
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.orderItemId} className="card p-4"
                ref={item.orderItemId === tappedItem ? tappedRef : undefined}>
                <div className="flex gap-3 items-center">
                  {item.imageUrl
                    ? <img src={item.imageUrl} alt={item.title} className="w-16 h-16 rounded-xl object-cover border border-border shrink-0" />
                    : <div className="w-16 h-16 rounded-xl bg-primary-50 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink">{item.title}</p>
                    {item.variantTitle && item.variantTitle !== 'Default Title' && (
                      <p className="text-xs text-ink-soft">{item.variantTitle}</p>
                    )}
                  </div>
                </div>
                {item.review ? (
                  <div className="mt-1.5" data-testid="link-rating">
                    <p className="text-xs text-ink-soft">
                      You rated <span className="text-amber-400">{'★'.repeat(item.review.rating)}</span>
                      <span className="text-gray-200">{'★'.repeat(5 - item.review.rating)}</span> · {STATUS_TEXT[item.review.status] || item.review.status}
                    </p>
                    {item.review.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.review.tags.map((t) => <span key={t} className="text-2xs bg-primary-50 text-primary px-2 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    )}
                  </div>
                ) : (
                  <RateItem itemId={item.orderItemId} entry={item}
                    defaultOpen={items.length === 1 || item.orderItemId === tappedItem}
                    initialRating={item.orderItemId === tappedItem ? tappedRating : 0}
                    submit={(payload) => submitLinkReview(token, payload)} onDone={load} />
                )}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink-muted mt-6 text-center">
            <Link to="/shop" className="hover:underline">Continue shopping</Link>
          </p>
        </>
      )}
    </div>
  );
}
